import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { DropzoneFile, UploaderProps } from '../components/Dropzone/utils';
import { DropzoneDelete, DropzoneUpload } from 'models/Dropzone';
import { FileType, fileTypeIcon } from 'components/Dropzone/utils';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { useTranslation } from 'react-i18next';
import { SUCCESS_MESSAGES } from 'request-responses';
import { getPercentageWithTwoDecimals, sentryError } from 'utils';

export type DropzoneFieldRequests = {
  uploadRequest: DropzoneUpload;
  deleteRequest: DropzoneDelete;
  fetchFiles?: () => Promise<DropzoneFile[]>;
};

type UseDropzoneField = {
  fileName: string;
  files: Array<DropzoneFile>;
  maxFiles?: number;
  onChange: (value: Array<DropzoneFile> | DropzoneFile) => void;
  updateFiles?: (value: DropzoneFile[]) => void;
  nameToUpload?: string;
  hasCustomNames?: boolean;
  sequential?: boolean;
} & Pick<DropzoneFieldRequests, 'uploadRequest'>;

const useDropzoneField = ({
  fileName,
  onChange,
  files,
  maxFiles,
  uploadRequest,
  hasCustomNames,
  updateFiles,
  nameToUpload,
  sequential,
}: UseDropzoneField) => {
  const [uploaderProps, setUploaderProps] = useState<
    Record<string, UploaderProps>
  >({});
  const { popServerError, popSuccess } = useContext(PopNotificationsContext);
  const { t } = useTranslation();

  const uploaderArray = useMemo(
    () => Object.values(uploaderProps),
    [uploaderProps],
  );

  const createFileConfig = useCallback(
    (file: File) => ({
      onUploadProgress: (progressEvent: ProgressEvent) => {
        const { name, type } = file;
        const { total, loaded } = progressEvent;
        const percentage = getPercentageWithTwoDecimals(loaded, total);

        setUploaderProps((old) => ({
          ...old,
          [name]: {
            percentage,
            name,
            size: total,
            icon: fileTypeIcon[type.split('/')[0] as FileType],
          },
        }));
      },
    }),
    [],
  );

  const deltaValue = useRef<DropzoneFile[]>([]);

  const updateUploadedFilesValue = useCallback(
    (newFiles: DropzoneFile[]) => {
      deltaValue.current = [...deltaValue.current, ...newFiles];
      const file = maxFiles === 1 ? deltaValue.current[0] : deltaValue.current;

      onChange?.(file);
      updateFiles?.(deltaValue.current);

      setUploaderProps((old) => {
        const newUP = { ...old };
        newFiles.forEach((newFile) => delete newUP[newFile?.name]);
        return newUP;
      });
    },
    [maxFiles, onChange, updateFiles],
  );

  const onDropUpload = useCallback(
    (filesToUpload: File[]) => {
      if (!uploadRequest) throw Error('Upload request is not passed as a prop');
      if (!filesToUpload || !filesToUpload.length) return;

      deltaValue.current = files;

      const uploadSingle = async (file: File) => {
        const arr = file.name.split('.');
        arr.pop();
        const fileNameTrimmed = arr.join('.');

        try {
          const savedFile = await uploadRequest(
            file,
            fileName,
            createFileConfig(file),
            hasCustomNames ? nameToUpload || fileNameTrimmed : '',
          );

          if (savedFile) {
            popSuccess(t(SUCCESS_MESSAGES.FILE_UPLOADED));
            updateUploadedFilesValue([savedFile]);
          } else {
            setUploaderProps(({ [file.name]: _, ...rest }) => rest);
          }
        } catch (e) {
          sentryError(e);
          popServerError(e);
          setUploaderProps(({ [file.name]: _, ...rest }) => rest);
        }
      };

      if (sequential) {
        // Show every selected file up front as "waiting", then upload
        // one-by-one so files keep their selection order and each upload's
        // server response is applied before the next request starts. Each
        // file's onUploadProgress overwrites its pending entry once its turn
        // starts, and updateUploadedFilesValue removes it when it completes.
        setUploaderProps((old) => ({
          ...old,
          ...Object.fromEntries(
            filesToUpload.map((file) => [
              file.name,
              {
                name: file.name,
                size: file.size,
                percentage: 0,
                isPending: true,
                icon: fileTypeIcon[file.type.split('/')[0] as FileType],
              },
            ]),
          ),
        }));

        (async () => {
          for (const file of filesToUpload) {
            await uploadSingle(file);
          }
        })();
      } else {
        filesToUpload.forEach(uploadSingle);
      }
    },
    [
      uploadRequest,
      files,
      fileName,
      createFileConfig,
      hasCustomNames,
      nameToUpload,
      popSuccess,
      t,
      updateUploadedFilesValue,
      popServerError,
      sequential,
    ],
  );

  return [uploaderArray, onDropUpload, deltaValue] as const;
};

export default useDropzoneField;
